import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react'

/**
 * Toast — the short message after an action. Audit: no shared toast; three
 * copy-pasted bottom toasts that never went away, a fourth that did, six inline
 * banners with different icons, and silence on most saves.
 *
 * The rule (DECISIONS.toastOnSave):
 *   success  → toast.success('บันทึกแล้ว')          gone after 3 s
 *   failure  → toast.error('บันทึกไม่สำเร็จ', …)    stays 6 s; ALSO keep the inline
 *              error next to the form when the user has to fix something
 *   undo     → toast.success('ลบแล้ว', { action: { label: 'เลิกทำ', onClick } })
 *
 * <ToastProvider> is mounted ONCE near the app root. Until it is, useToast()
 * returns a no-op so a kit page never crashes.
 */
const Ctx = createContext(null)
let seq = 0

const TONE = {
  success: { Icon: CheckCircle2, icon: 'text-emerald-400', ms: 3000 },
  error: { Icon: AlertTriangle, icon: 'text-rose-400', ms: 6000 },
  info: { Icon: Info, icon: 'text-sky-300', ms: 3500 },
}

export function ToastProvider({ children }) {
  const [items, setItems] = useState([])
  const timers = useRef(new Map())

  const dismiss = useCallback((id) => {
    clearTimeout(timers.current.get(id))
    timers.current.delete(id)
    setItems((s) => s.filter((t) => t.id !== id))
  }, [])

  const push = useCallback((tone, message, opts = {}) => {
    const id = ++seq
    setItems((s) => [...s, { id, tone, message, detail: opts.detail, action: opts.action }].slice(-3))
    timers.current.set(id, setTimeout(() => dismiss(id), opts.duration ?? TONE[tone].ms))
    return id
  }, [dismiss])

  const api = useMemo(() => ({
    success: (m, o) => push('success', m, o),
    error: (m, o) => push('error', m, o),
    info: (m, o) => push('info', m, o),
    dismiss,
  }), [push, dismiss])

  return (
    <Ctx.Provider value={api}>
      {children}
      <div data-toast-stack className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] flex flex-col items-center gap-2 pointer-events-none" aria-live="polite">
        {items.map((t) => {
          const { Icon, icon } = TONE[t.tone]
          return (
            <div key={t.id} role={t.tone === 'error' ? 'alert' : 'status'}
              className="pointer-events-auto flex items-center gap-2.5 max-w-[min(92vw,520px)] rounded-xl bg-slate-900 text-white pl-3.5 pr-2 py-2.5 shadow-xl shadow-slate-900/30 animate-ss-pop-in">
              <Icon size={16} className={`flex-none ${icon}`} />
              <div className="min-w-0">
                <div className="text-[13.5px] font-medium break-words">{t.message}</div>
                {t.detail && <div className="text-[12px] text-slate-300 break-words">{t.detail}</div>}
              </div>
              {t.action && (
                <button type="button" onClick={() => { t.action.onClick?.(); dismiss(t.id) }}
                  className="flex-none ml-1 px-2 py-1 rounded-lg text-[13px] font-semibold text-sky-300 hover:bg-white/10">
                  {t.action.label}
                </button>
              )}
              <button type="button" onClick={() => dismiss(t.id)} aria-label="ปิด"
                className="flex-none w-7 h-7 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 flex items-center justify-center">
                <X size={14} />
              </button>
            </div>
          )
        })}
      </div>
    </Ctx.Provider>
  )
}

const NOOP = { success: () => 0, error: () => 0, info: () => 0, dismiss: () => {} }
let warned = false

export function useToast() {
  const ctx = useContext(Ctx)
  if (!ctx && !warned) { warned = true; console.warn('[kit] useToast() outside <ToastProvider> — messages are dropped') }
  return ctx || NOOP
}
